import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceTimeline from './components/ServiceTimeline';
import InteractiveMap from './components/InteractiveMap';
import ServiceDetailsPanel from './components/ServiceDetailsPanel';
import NotificationPanel from './components/NotificationPanel';
import ActionButtons from './components/ActionButtons';
import { trackingService } from '../../services/trackingService';

const PublicServiceTracking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Real-time state from Supabase
  const [appointment, setAppointment] = useState(null);
  const [technician, setTechnician] = useState(null);
  const [technicianLocation, setTechnicianLocation] = useState(null);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [currentStatus, setCurrentStatus] = useState('');
  const [statusHistory, setStatusHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [eta, setEta] = useState(null);

  const appointmentRef = searchParams?.get('ref') || 'INT-2025-12-21-001';

  // Load appointment data
  useEffect(() => {
    const loadAppointmentData = async () => {
      try {
        setIsLoading(true);
        const data = await trackingService?.getAppointmentDetails(appointmentRef);
        
        setAppointment(data?.appointment);
        setTechnician(data?.technician);
        setCurrentStatus(data?.appointment?.status);
        setStatusHistory(data?.statusHistory || []);
        setNotifications(data?.notifications || []);
        
        if (data?.technician?.currentLatitude && data?.technician?.currentLongitude) {
          setTechnicianLocation({
            lat: data?.technician?.currentLatitude,
            lng: data?.technician?.currentLongitude
          });
        }
        
        if (data?.appointment?.customerLatitude && data?.appointment?.customerLongitude) {
          setCustomerLocation({
            lat: data?.appointment?.customerLatitude,
            lng: data?.appointment?.customerLongitude
          });
        }

        // Calculate initial ETA
        if (data?.appointment?.id) {
          const calculatedEta = await trackingService?.calculateETA(data?.appointment?.id);
          setEta(calculatedEta);
        }
      } catch (err) {
        setError(err?.message || 'Failed to load tracking data');
        console.error('Error loading appointment:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAppointmentData();
  }, [appointmentRef]);

  // Subscribe to real-time location updates
  useEffect(() => {
    if (!appointment?.id) return;

    const unsubscribeLocation = trackingService?.subscribeToTechnicianLocation(
      appointment?.id,
      async (locationData) => {
        setTechnicianLocation({
          lat: locationData?.latitude,
          lng: locationData?.longitude
        });

        // Recalculate ETA when location updates
        try {
          const newEta = await trackingService?.calculateETA(appointment?.id);
          setEta(newEta);
        } catch (err) {
          console.error('Error calculating ETA:', err);
        }
      }
    );

    return () => {
      unsubscribeLocation();
    };
  }, [appointment?.id]);

  // Subscribe to real-time status updates
  useEffect(() => {
    if (!appointment?.id) return;

    const unsubscribeStatus = trackingService?.subscribeToAppointmentStatus(
      appointment?.id,
      (statusData) => {
        setCurrentStatus(statusData?.status);
        setAppointment((prev) => ({
          ...prev,
          status: statusData?.status,
          estimatedArrivalTime: statusData?.estimatedArrivalTime,
          actualArrivalTime: statusData?.actualArrivalTime,
          completionTime: statusData?.completionTime
        }));

        // Reload status history
        trackingService?.getAppointmentDetails(appointmentRef)?.then((data) => {
          setStatusHistory(data?.statusHistory || []);
        });
      }
    );

    return () => {
      unsubscribeStatus();
    };
  }, [appointment?.id, appointmentRef]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!appointment?.id) return;

    const unsubscribeNotifications = trackingService?.subscribeToNotifications(
      appointment?.id,
      (newNotification) => {
        setNotifications((prev) => [
          {
            id: newNotification?.id,
            type: newNotification?.notification_type,
            title: newNotification?.title,
            message: newNotification?.message,
            timestamp: new Date(newNotification?.created_at),
            actionable: newNotification?.actionable
          },
          ...prev
        ]);
      }
    );

    return () => {
      unsubscribeNotifications();
    };
  }, [appointment?.id]);

  const handleReschedule = () => {
    console.log('Reprogrammer l\'intervention');
    navigate('/service-booking-wizard');
  };

  const handleContactSupport = () => {
    console.log('Contacter le support');
  };

  const handleCancelService = () => {
    console.log('Annuler l\'intervention');
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement de votre suivi...</p>
        </div>
      </div>);

  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="AlertCircle" size={32} className="text-error" />
            <h2 className="text-xl font-semibold text-foreground">Erreur de chargement</h2>
          </div>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location?.reload()} variant="outline">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const serviceData = appointment ? {
    reference: appointment?.reference,
    date: appointment?.scheduledDate,
    timeSlot: `${appointment?.scheduledTimeStart?.substring(0, 5)} - ${appointment?.scheduledTimeEnd?.substring(0, 5)}`,
    urgency: appointment?.urgency,
    address: {
      street: appointment?.customerAddress?.split(',')?.[0],
      postalCode: appointment?.customerAddress?.split(',')?.[1]?.trim()?.split(' ')?.[0] || '',
      city: appointment?.customerAddress?.split(',')?.[1]?.trim()?.substring(5) || ''
    },
    services: [
      { name: appointment?.serviceType, price: appointment?.priceSubtotal }
    ],
    pricing: {
      items: [
        {
          name: appointment?.serviceType,
          description: appointment?.notes || 'Service professionnel',
          price: appointment?.priceSubtotal
        }
      ],
      subtotal: appointment?.priceSubtotal,
      vatRate: 10,
      vat: appointment?.priceVat,
      total: appointment?.priceTotal
    }
  } : null;

  const technicianData = technician ? {
    name: technician?.name,
    photo: technician?.avatarUrl,
    photoAlt: `Professional headshot of ${technician?.name}, ${technician?.specialty}`,
    specialty: technician?.specialty,
    rating: technician?.rating,
    reviews: technician?.reviewCount
  } : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80 shadow-md">
                <Icon name="Droplet" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-foreground">PlumberPro</h1>
                <p className="text-xs text-muted-foreground">Suivi de votre intervention</p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              iconName="HelpCircle"
              onClick={handleContactSupport}>

              Aide
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <ServiceTimeline
            currentStatus={currentStatus}
            statusHistory={statusHistory} />

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {technicianLocation && customerLocation &&
            <InteractiveMap
              technicianLocation={technicianLocation}
              customerLocation={customerLocation}
              eta={eta} />

            }

            <NotificationPanel notifications={notifications} />
          </div>

          <div className="space-y-6">
            {serviceData && technicianData &&
            <ServiceDetailsPanel
              serviceDetails={serviceData}
              technician={technicianData} />

            }

            <ActionButtons
              onReschedule={handleReschedule}
              onContactSupport={handleContactSupport}
              onCancelService={handleCancelService} />

          </div>
        </div>

        <div className="mt-8 p-6 bg-card rounded-lg border border-border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-foreground mb-2">
                Vos données sont protégées
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Vos données et votre localisation ne sont partagées qu'avec votre accord. Nous respectons strictement le RGPD et vos informations sont sécurisées.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">
                  Conforme RGPD
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">
                  Données chiffrées
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">
                  Paiement sécurisé
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer className="bg-card border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80">
                  <Icon name="Droplet" size={18} color="#FFFFFF" />
                </div>
                <span className="font-semibold text-foreground">PlumberPro</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Service de plomberie professionnel avec suivi en temps réel et transparence totale.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-3">Contact</h4>
              <div className="space-y-2">
                <a href="tel:+33123456789" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="Phone" size={16} />
                  +33 1 23 45 67 89
                </a>
                <a href="mailto:contact@plumberpro.fr" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="Mail" size={16} />
                  contact@plumberpro.fr
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-3">Liens rapides</h4>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/service-booking-wizard')}
                  className="block text-sm text-muted-foreground hover:text-foreground transition-colors">

                  Réserver un service
                </button>
                <button
                  onClick={handleContactSupport}
                  className="block text-sm text-muted-foreground hover:text-foreground transition-colors">

                  Support client
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} PlumberPro. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default PublicServiceTracking;